import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getJson } from '@/lib/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Shield, User, History, Settings, AlertTriangle, CheckCircle, Smartphone, Send, Trash2 } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';

interface User {
  id: number;
  username: string;
  name: string;
  email: string | null;
  role: string;
  roles: string[];
  region: string | null;
  isActive: boolean;
  mobileTestModeEnabled?: boolean;
  createdAt: string;
}

interface RoleAudit {
  id: number;
  changedByUser: string;
  changedByUsername: string;
  targetUser: string;
  targetUsername: string;
  oldRole: string;
  newRole: string;
  reason: string | null;
  ipAddress: string | null;
  createdAt: string;
}

interface RolePermissions {
  userId: number;
  username: string;
  roles: string[];
  isSuperAdmin: boolean;
  canAssignRoles: {
    operator: boolean;
    admin: boolean;
    superadmin: boolean;
  };
  canAccessFeatures: {
    evidenceMonitor: boolean;
    emergencyRoutes: boolean;
    roleAuditLog: boolean;
    systemHealth: boolean;
  };
}

interface PermissionsResponse {
  success: boolean;
  permissions: RolePermissions;
}

interface UsersResponse {
  success: boolean;
  users: User[];
}

interface AuditResponse {
  success: boolean;
  auditLog: RoleAudit[];
}

interface ChangeRoleResponse {
  message: string;
}

interface MobileTestCycle {
  cycleId: number;
  cycleName: string;
  retailerId: number | null;
  retailerName: string;
}

interface MobileTestOperator {
  id: number;
  name: string;
  email: string | null;
  row_count?: number;
  store_count?: number;
}

interface MobileTestCopy {
  test_operator_id: number;
  test_operator_name: string;
  source_operator_id: number;
  source_operator_name: string;
  cycle_id: number;
  cycle_name: string;
  retailer_name?: string | null;
  row_count: number;
  store_count: number;
  copied_at: string;
}

export default function SuperAdminManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [selectedRetailerId, setSelectedRetailerId] = useState<string>('all');
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [selectedSourceOperatorId, setSelectedSourceOperatorId] = useState<string>('');
  const [selectedTargetOperatorId, setSelectedTargetOperatorId] = useState<string>('');

  // Get current user's permissions
  const { data: permissions, isLoading: permissionsLoading } = useQuery<PermissionsResponse>({
    queryKey: ['/api/superadmin/role-permissions'],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/superadmin/role-permissions');
      const data = await getJson<PermissionsResponse>('/api/superadmin/role-permissions');
      console.log('[QUERY_DEBUG] /api/superadmin/role-permissions response:', data);
      return data;
    },
    retry: false
  });

  // Get all users
  const { data: usersResponse, isLoading: usersLoading } = useQuery<UsersResponse>({
    queryKey: ['/api/superadmin/users'],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/superadmin/users');
      const data = await getJson<UsersResponse>('/api/superadmin/users');
      console.log('[QUERY_DEBUG] /api/superadmin/users response:', data?.users?.length || 0, 'users');
      return data;
    },
    enabled: permissions?.permissions?.isSuperAdmin || permissions?.permissions?.roles?.includes('admin'),
    retry: false
  });

  // Get role audit log (SuperAdmin only)
  const { data: auditResponse, isLoading: auditLoading } = useQuery<AuditResponse>({
    queryKey: ['/api/superadmin/role-audit'],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/superadmin/role-audit');
      const data = await getJson<AuditResponse>('/api/superadmin/role-audit');
      console.log('[QUERY_DEBUG] /api/superadmin/role-audit response:', data?.auditLog?.length || 0, 'entries');
      return data;
    },
    enabled: permissions?.permissions?.isSuperAdmin,
    retry: false
  });

  const { data: mobileCyclesResponse } = useQuery<{ success: boolean; cycles: MobileTestCycle[] }>({
    queryKey: ['/api/superadmin/mobile-test-cycles'],
    queryFn: async () => getJson('/api/superadmin/mobile-test-cycles'),
    enabled: permissions?.permissions?.isSuperAdmin,
    retry: false,
  });

  const { data: mobileTargetsResponse } = useQuery<{ success: boolean; targets: User[] }>({
    queryKey: ['/api/superadmin/mobile-test-targets'],
    queryFn: async () => getJson('/api/superadmin/mobile-test-targets'),
    enabled: permissions?.permissions?.isSuperAdmin,
    retry: false,
  });

  const { data: mobileCopiesResponse } = useQuery<{ success: boolean; copies: MobileTestCopy[] }>({
    queryKey: ['/api/superadmin/mobile-test-copies'],
    queryFn: async () => getJson('/api/superadmin/mobile-test-copies'),
    enabled: permissions?.permissions?.isSuperAdmin,
    retry: false,
  });

  const { data: sourceOperatorsResponse } = useQuery<{ success: boolean; operators: MobileTestOperator[] }>({
    queryKey: ['/api/superadmin/mobile-test-source-operators', selectedCycleId],
    queryFn: async () => {
      const cycleId = selectedCycleId?.trim();
      if (!cycleId) return { success: true, operators: [] };
      return getJson(`/api/superadmin/mobile-test-source-operators/${cycleId}`);
    },
    enabled: permissions?.permissions?.isSuperAdmin && /^\d+$/.test(selectedCycleId),
    retry: false,
  });

  // Change role mutation
  const changeRoleMutation = useMutation<ChangeRoleResponse, Error, { targetUserId: number; newRole: string; reason?: string }>({
    mutationFn: async (data: { targetUserId: number; newRole: string; reason?: string }) => {
      const response = await apiRequest('/api/superadmin/change-role', 'POST', data);
      return (await response.json()) as ChangeRoleResponse;
    },
    onSuccess: (response) => {
      toast({
        title: "Role Changed Successfully",
        description: response.message,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/role-audit'] });
      setShowChangeDialog(false);
      setSelectedUser(null);
      setNewRole('');
      setReason('');
    },
    onError: (error: any) => {
      toast({
        title: "Role Change Failed",
        description: error.message || 'Failed to change user role',
        variant: "destructive",
      });
    },
  });

  const copyMobilePayloadMutation = useMutation({
    mutationFn: async (data: { cycleId: number; sourceOperatorId: number; testOperatorId: number }) => {
      const response = await apiRequest('/api/superadmin/mobile-test-copy', 'POST', data);
      return response.json();
    },
    onSuccess: (response: any) => {
      toast({
        title: 'Test Copy Sent',
        description: `${response.copied} rows copied to the test account.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/mobile-test-copies'] });
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/mobile-test-targets'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Test Copy Failed',
        description: error.message || 'Unable to create test copy',
        variant: 'destructive',
      });
    },
  });

  const toggleMobileTestModeMutation = useMutation({
    mutationFn: async (data: { userId: number; enabled: boolean }) => {
      const response = await apiRequest('/api/superadmin/mobile-test-mode', 'POST', data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/mobile-test-targets'] });
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/users'] });
    },
  });

  const clearMobileCopyMutation = useMutation({
    mutationFn: async (data: { testOperatorId: number; cycleId?: number }) => {
      const response = await apiRequest('/api/superadmin/mobile-test-copy', 'DELETE', data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Test Copy Cleared',
        description: 'The copied mobile payload was removed.',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/superadmin/mobile-test-copies'] });
    },
    onError: (error: any) => {
      toast({
        title: 'Clear Failed',
        description: error.message || 'Unable to clear test copy',
        variant: 'destructive',
      });
    },
  });

  const handleRoleChange = () => {
    if (!selectedUser || !newRole) return;
    
    changeRoleMutation.mutate({
      targetUserId: selectedUser.id,
      newRole,
      reason: reason.trim() || undefined
    });
  };

  const openChangeDialog = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setReason('');
    setShowChangeDialog(true);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin': return 'bg-red-500 hover:bg-red-600';
      case 'admin': return 'bg-blue-500 hover:bg-blue-600';
      case 'operator': return 'bg-green-500 hover:bg-green-600';
      default: return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (permissionsLoading) {
    return <div className="flex justify-center p-8">Loading permissions...</div>;
  }

  if (!permissions?.success || (!permissions.permissions.isSuperAdmin && !permissions.permissions.roles.includes('admin'))) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <AlertTriangle className="h-16 w-16 text-red-500" />
        <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
        <p className="text-gray-600 text-center max-w-md">
          You need Admin or SuperAdmin privileges to access this page.
        </p>
      </div>
    );
  }

  const userPermissions = permissions.permissions;
  const mobileCycles = mobileCyclesResponse?.cycles || [];
  const retailers = Array.from(new Map(
    mobileCycles
      .filter((cycle) => cycle.retailerId)
      .map((cycle) => [cycle.retailerId, { retailerId: cycle.retailerId!, retailerName: cycle.retailerName }])
  ).values());
  const filteredCycles = selectedRetailerId === 'all'
    ? mobileCycles
    : mobileCycles.filter((cycle) => String(cycle.retailerId) === selectedRetailerId);
  const targetOperators = mobileTargetsResponse?.targets || [];
  const activeCopies = mobileCopiesResponse?.copies || [];

  const handleSendTestCopy = () => {
    if (!selectedCycleId || !selectedSourceOperatorId || !selectedTargetOperatorId) {
      toast({
        title: 'Selection Required',
        description: 'Choose a cycle, source operator, and target test account first.',
        variant: 'destructive',
      });
      return;
    }

    copyMobilePayloadMutation.mutate({
      cycleId: Number(selectedCycleId),
      sourceOperatorId: Number(selectedSourceOperatorId),
      testOperatorId: Number(selectedTargetOperatorId),
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center space-x-3">
        <Shield className="h-8 w-8 text-red-500" />
        <div>
          <h1 className="text-3xl font-bold">SuperAdmin Management</h1>
          <p className="text-gray-600">
            Manage user roles and system access - Current Role: {userPermissions.isSuperAdmin ? 'SuperAdmin' : 'Admin'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users" className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>User Management</span>
          </TabsTrigger>
          {userPermissions.isSuperAdmin && (
            <TabsTrigger value="audit" className="flex items-center space-x-2">
              <History className="h-4 w-4" />
              <span>Audit Log</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="permissions" className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>My Permissions</span>
          </TabsTrigger>
          {userPermissions.isSuperAdmin && (
            <TabsTrigger value="mobile-test" className="flex items-center space-x-2">
              <Smartphone className="h-4 w-4" />
              <span>Mobile Test Mode</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>User Role Management</CardTitle>
              <CardDescription>
                Manage user roles and access levels. {userPermissions.isSuperAdmin ? 'SuperAdmins' : 'Admins'} can assign roles to users.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="text-center py-8">Loading users...</div>
              ) : usersResponse?.success ? (
                <div className="space-y-4">
                  {usersResponse.users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold">{user.name}</span>
                            <Badge variant="outline">@{user.username}</Badge>
                            {!user.isActive && (
                              <Badge variant="destructive">Archived</Badge>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email && <span>{user.email} • </span>}
                            {user.region && <span>Region: {user.region} • </span>}
                            <span>Created: {formatDateTime(user.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Badge className={getRoleBadgeColor(user.role)}>
                          {user.role.toUpperCase()}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openChangeDialog(user)}
                          disabled={user.id === userPermissions.userId} // Prevent self role change
                        >
                          Change Role
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-red-500">
                  Failed to load users. Please check your permissions.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {userPermissions.isSuperAdmin && (
          <TabsContent value="audit">
            <Card>
              <CardHeader>
                <CardTitle>Role Change Audit Log</CardTitle>
                <CardDescription>
                  Complete history of all role changes in the system (SuperAdmin only).
                </CardDescription>
              </CardHeader>
              <CardContent>
                {auditLoading ? (
                  <div className="text-center py-8">Loading audit log...</div>
                ) : auditResponse?.success ? (
                  <div className="space-y-4">
                    {auditResponse.auditLog.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No role changes recorded yet.
                      </div>
                    ) : (
                      auditResponse.auditLog.map((audit) => (
                        <div key={audit.id} className="p-4 border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Badge variant="outline">{audit.oldRole} → {audit.newRole}</Badge>
                              <span className="font-medium">{audit.targetUser}</span>
                              <span className="text-sm text-gray-500">(@{audit.targetUsername})</span>
                            </div>
                            <span className="text-sm text-gray-500">
                              {formatDateTime(audit.createdAt)}
                            </span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-600">Changed by: </span>
                            <span className="font-medium">{audit.changedByUser}</span>
                            <span className="text-gray-500"> (@{audit.changedByUsername})</span>
                            {audit.ipAddress && (
                              <span className="text-gray-500"> from {audit.ipAddress}</span>
                            )}
                          </div>
                          {audit.reason && (
                            <div className="text-sm">
                              <span className="text-gray-600">Reason: </span>
                              <span className="italic">{audit.reason}</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-red-500">
                    Failed to load audit log.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="permissions">
          <Card>
            <CardHeader>
              <CardTitle>My Permissions & Access</CardTitle>
              <CardDescription>
                Your current role and system access permissions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Current Role</h3>
                <Badge className={getRoleBadgeColor(userPermissions.roles[0] || 'operator')}>
                  {userPermissions.isSuperAdmin ? 'SUPERADMIN' : userPermissions.roles[0]?.toUpperCase() || 'OPERATOR'}
                </Badge>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Role Assignment Permissions</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAssignRoles.operator ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>Assign Operator</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAssignRoles.admin ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>Assign Admin</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAssignRoles.superadmin ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>Assign SuperAdmin</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Feature Access</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAccessFeatures.evidenceMonitor ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>Evidence Monitor</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAccessFeatures.emergencyRoutes ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>Emergency Routes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAccessFeatures.roleAuditLog ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>Role Audit Log</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {userPermissions.canAccessFeatures.systemHealth ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span>System Health</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {userPermissions.isSuperAdmin && (
          <TabsContent value="mobile-test" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Send a Read-Only Test Copy</CardTitle>
                <CardDescription>
                  Pick a live mobile payload and copy it to a test operator account without touching real ownership or live mobile assignments.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Retailer</label>
                    <Select value={selectedRetailerId} onValueChange={(value) => {
                      setSelectedRetailerId(value);
                      setSelectedCycleId('');
                      setSelectedSourceOperatorId('');
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="All retailers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All retailers</SelectItem>
                        {retailers.map((retailer) => (
                          <SelectItem key={retailer.retailerId} value={String(retailer.retailerId)}>
                            {retailer.retailerName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Cycle</label>
                    <Select value={selectedCycleId} onValueChange={(value) => {
                      setSelectedCycleId(value);
                      setSelectedSourceOperatorId('');
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose cycle" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredCycles.map((cycle) => (
                          <SelectItem key={cycle.cycleId} value={String(cycle.cycleId)}>
                            {cycle.cycleName} · {cycle.retailerName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Source Operator</label>
                    <Select value={selectedSourceOperatorId} onValueChange={setSelectedSourceOperatorId} disabled={!selectedCycleId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose source operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {(sourceOperatorsResponse?.operators || []).map((operator) => (
                          <SelectItem key={operator.id} value={String(operator.id)}>
                            {operator.name} · {operator.store_count || 0} stores
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Target Test Account</label>
                    <Select value={selectedTargetOperatorId} onValueChange={setSelectedTargetOperatorId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose target account" />
                      </SelectTrigger>
                      <SelectContent>
                        {targetOperators.map((operator) => (
                          <SelectItem key={operator.id} value={String(operator.id)}>
                            {operator.name}{operator.mobileTestModeEnabled ? ' · Test mode on' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-4">
                  <div>
                    <p className="font-medium">Read-only by design</p>
                    <p className="text-sm text-muted-foreground">
                      Test accounts can preview copied cycles on device, but mobile uploads, visit starts, completion, and signatures are blocked.
                    </p>
                  </div>
                  <Button onClick={handleSendTestCopy} disabled={copyMobilePayloadMutation.isPending}>
                    <Send className="mr-2 h-4 w-4" />
                    Send Test Copy
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Test Accounts</CardTitle>
                <CardDescription>Turn test mode on for the accounts you use on device.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {targetOperators.map((operator) => (
                  <div key={operator.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <div className="font-medium">{operator.name}</div>
                      <div className="text-sm text-muted-foreground">{operator.email}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={operator.mobileTestModeEnabled ? 'default' : 'outline'}>
                        {operator.mobileTestModeEnabled ? 'Test mode enabled' : 'Normal operator mode'}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleMobileTestModeMutation.mutate({
                          userId: operator.id,
                          enabled: !operator.mobileTestModeEnabled,
                        })}
                      >
                        {operator.mobileTestModeEnabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Active Test Copies</CardTitle>
                <CardDescription>Clear and resend whenever you want a fresh mobile payload.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {activeCopies.length === 0 && (
                  <p className="text-sm text-muted-foreground">No active test copies yet.</p>
                )}
                {activeCopies.map((copy) => (
                  <div key={`${copy.test_operator_id}-${copy.cycle_id}`} className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <div className="font-medium">{copy.test_operator_name} ← {copy.source_operator_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {copy.retailer_name ? `${copy.retailer_name} · ` : ''}
                        {copy.cycle_name} · {copy.store_count} stores · {copy.row_count} rows · {new Date(copy.copied_at).toLocaleString()}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => clearMobileCopyMutation.mutate({
                        testOperatorId: copy.test_operator_id,
                        cycleId: copy.cycle_id,
                      })}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Clear
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Role Change Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent className="z-[100]">
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedUser?.name} (@{selectedUser?.username}).
              {!userPermissions?.canAssignRoles?.superadmin && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                  Note: You cannot assign SuperAdmin role with your current permissions.
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">New Role</label>
              <select 
                value={newRole} 
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select new role</option>
                <option value="operator">Operator</option>
                <option value="admin">Admin</option>
                <option 
                  value="superadmin" 
                  disabled={!userPermissions?.canAssignRoles?.superadmin}
                >
                  SuperAdmin {!userPermissions?.canAssignRoles?.superadmin ? '(Restricted)' : ''}
                </option>
              </select>
              <div className="text-xs text-gray-500 mt-1">
                Debug: Permissions loaded: {userPermissions ? 'Yes' : 'No'} | Can assign SuperAdmin: {userPermissions?.canAssignRoles?.superadmin ? 'Yes' : 'No'}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Reason (Optional)</label>
              <Textarea
                placeholder="Explain why this role change is needed..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRoleChange}
              disabled={!newRole || newRole === selectedUser?.role || changeRoleMutation.isPending}
            >
              {changeRoleMutation.isPending ? 'Changing...' : 'Change Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
